import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TimeoutService } from '@df/ng/timeout.service';
import { StandardSerializer } from '@df/util/fn/standard-serializer';
import { Url } from '@df/core/service/url';
import { AbstractStorageService } from '@df/browser/storage/abstract-storage.service';

/**
 * Allows to manipulate the URL hash - to save and retrieve data.
 * It uses the hash as sort of storage.
 *
 * Just inject this service and do:
 * this.hashStorageService.get([hash_attribute_name]);
 *
 * or
 * this.hashStorageService.set([hash_attribute_name], [hash_value_to_be_set]);
 *
 */
@Injectable({
    providedIn: 'root'
})
export class HashStorageService extends AbstractStorageService {
    /**
     * Here we keep the current state of hash. This object is the source of truth.
     * @private
     */
    private hashData: any;

    /**
     * This flag is set to true just for a short moment, when the router is changing (a new page is loaded).
     * This is not to permanently disable hashes!
     * @private
     */
    isTemporarilyBlocked = false;

    /**
     * Debounced to avoid too many URL changes when we are setting numerous hash properties one by one.
     * @private
     */

    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private url = inject(Url);
    private location = inject(Location);
    private timeoutService = inject(TimeoutService);
    private applyToUrlDebounced = this.timeoutService.debounce(() => this.applyToUrl(), 20);

    private applyToUrl() {
        this.setHash(this.hashData ? <string>StandardSerializer.serialize(this.hashData, '', undefined, { noescape: true }) : '');
    }

    constructor() {
        super();
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this.isTemporarilyBlocked = true;
                this.hashData = undefined;
            }
            if (event instanceof NavigationEnd) {
                this.isTemporarilyBlocked = false;
            }
        });

        if (typeof window !== 'undefined') {
            window.addEventListener('hashchange', () => {
                this.hashData = undefined;
            });
        }
    }

    /**
     * Return locally buffered version of the hash data (if already created) or load it from the url and store locally.
     * This way the actual
     * @protected
     */
    protected getData(): any {
        if (!this.hashData) {
            this.hashData = StandardSerializer.unserialize(this.getHash()) || {};
        }
        return this.hashData;
    }

    /**
     * It replaces the local version of the hash data and puts it into the URL
     * @param data
     * @protected
     */
    protected setData(data: any, immediate = false) {
        // Make sure the hash fields are always in the same order
        // to avoid generating different URLs having the same meaning.
        this.hashData = Object.keys(data)
            .sort()
            .reduce((obj, key) => {
                obj[key] = data[key];
                return obj;
            }, {});

        this.isTemporarilyBlocked = false;
        if (immediate) {
            this.applyToUrl();
        } else {
            this.applyToUrlDebounced();
        }
    }

    /**
     * Returns raw hash string taken directly from the URL or Router (whichever has the most recent version)
     */
    getHash(): string {
        // we cant use method below as it does not have data till scopeApply
        // it pulls previous data, and so removes any chagnes we made in last actions
        // return decodeURIComponent(window.location.hash.substr(1));
        return this.getUrlHash() || this.activatedRoute.snapshot.fragment || '';
    }

    private getUrlHash(): string {
        return typeof window !== 'undefined' ? window.location.hash?.substring(1) : '';
    }

    /**
     * Performs the logic of putting the serialized hash into the actual URL fragment.
     * The only logic should prevent replacing the hash with the same value.
     * @param hash
     * @protected
     */

    setHash(hash: string) {
        if (this.isTemporarilyBlocked) {
            return;
        }
        const existingHash = this.getUrlHash();
        if (hash !== existingHash) {
            // # version 1
            // window.history.replaceState(window.history.state, document.title, window.location.pathname + '#' + (hash || '_'));

            // # version 2
            // Select filters on listing -> go to product page -> go back - hash is gone. This is the fix.
            // the way it is implement it causes route reloads

            // if hash is read immediately after then location valu is not set yet and hash is flushed

            // # version 3
            // causes multiple problems cause if changin angular route on every hash update
            // const params = this.activatedRoute.snapshot.queryParams;
            // this.router.navigateByUrl(this.url.getPath() + (hash ? '#' + hash : ''), {
            //     replaceUrl: true
            // });
            this.location.replaceState(this.url.getPath() + (hash ? '#' + hash : ''));

            // console.log('navigate', app.url.getPath(), {
            //     queryParams: this.activatedRoute.snapshot.queryParams,
            //     fragment: hash,
            //     replaceUrl: true
            // });
        }
    }
}
